// @erp/module-lib/fe · queryOptions accessor-factories (DEV-GUIDE-002 §3.2).
//
// THE PATTERN these prove: each factory takes PLAIN values (never an accessor)
// and is CALLED inside the options function at the screen —
//   const search = Route.useSearch();               // accessor
//   const q = useQuery(() => libraryQueries.patrons(search()));   // called here
// Because the whole options function re-runs when search() changes, reactivity
// lives at the call site and the factory stays a pure value→queryOptions map.
// (Passing `libraryQueries.patrons(search())` straight into useQuery — no arrow —
// snapshots the filters once and the query never re-keys. §3.2's cardinal sin.)
//
// Every queryFn is `() => unwrap<DTO>(libApi.<path>.$get(...))`: the typed hc
// client builds the request, unwrap resolves ok→JSON or throws the wire's
// ApiError (@erp/api-client). DTOs are the schema-inferred wire types — one
// contract for validator, service return, and this cache.
//
// staleTime tiers (ADR-001 §10.2 — factories override the portal's 30s default):
//   list      30s  — the listings default; a page is fine slightly stale
//   detail    60s  — a single row churns less than a filtered list
//   reference  5m  — registries (unpaged, rarely edited)
//   lookup     5s  — the desk barcode scan is near-live; the POS re-scans fast
// keepPreviousData on keyset lists holds the current page visible while the next
// cursor loads (no flash to empty on paginate/filter).
import { infiniteQueryOptions, keepPreviousData, queryOptions } from "@tanstack/solid-query";
import { unwrap } from "@erp/api-client";
import type {
  Author,
  AuthorDuplicatesResult,
  AuthorListQuery,
  Book,
  BookDetail,
  BookListQuery,
  Branch,
  CatalogueSearchQuery,
  CatalogueStats,
  AuthorCodeLookup,
  CodeLookup,
  PublisherMatch,
  Copy,
  CopyLookup,
  Ebook,
  EbookListQuery,
  Fine,
  FineListQuery,
  Hold,
  HoldListQuery,
  Library,
  Loan,
  LoanListQuery,
  Member,
  MemberAccount,
  MemberListQuery,
  MeResponse,
  MyAccount,
  MyFineListQuery,
  MyHoldListQuery,
  MyLoanListQuery,
  OpacSettings,
  PatronGroup,
  Publisher,
  PublisherListQuery,
  Shelf,
  Subject,
  Tag,
  // ── L2 locations tail (library detail + the zone/stack levels) ──
  Stack,
  Zone,
  // ── L5 digital ──
  BatchAssetUrlsResult,
  PresignedGet,
  ReadingSession,
  SessionListQuery,
  StorageUsage,
  // ── L6 serials / transit / stocktake ──
  AuditAlert,
  AuditAlertListQuery,
  AuditSession,
  AuditSessionListQuery,
  Serial,
  SerialIssue,
  SerialIssueListQuery,
  SerialListQuery,
  StocktakeScan,
  StocktakeScanListQuery,
  Transit,
  TransitListQuery,
  // ── L7 acquisitions ──
  BudgetLine,
  BudgetLineListQuery,
  GoodsReceipt,
  GRNListQuery,
  POListQuery,
  PurchaseOrder,
  ReceivableListQuery,
  Vendor,
  VendorListQuery,
  // ── Reports ──
  BillingMember,
  CalendarException,
  FlaggedCopy,
  CirculationRule,
  MemberBillingQuery,
  MemberPayment,
  MembershipPlan,
  PaymentListQuery,
  RevenueSummary,
  AuditEvent,
  AuditListQuery,
  ImportBatch,
  ImportBatchListQuery,
  GuardianLink,
  LibSettings,
  ParentalProfile,
  ReportAcquisitions,
  ReportCatalogue,
  ReportCirculation,
  ReportDigital,
  ReportFines,
  ReportMembers,
  ReportOverview,
  // ── The six REGISTER STATUS COUNTS (2026-09-22) ──
  AuditEventStats,
  CardStats,
  HoldStats,
  SerialStats,
  StocktakeStats,
  TransitStats,
} from "../schemas/index.js";
import { libApi } from "./client.js";
import { libraryKeys } from "./keys.js";

/** Keyset page envelope (@erp/core-schemas CursorMeta) — every cursor-paged list returns this. */
export type KeysetPage<T> = { data: T[]; nextCursor: string | null };

const STALE = {
  list: 30_000,
  detail: 60_000,
  reference: 5 * 60_000,
  lookup: 5_000,
  /** Presigned display-asset URLs — 240s, under the 300s GET lease (a cache hit must never serve an expired URL). */
  asset: 240_000,
} as const;

/**
 * ONE frozen instance. A fresh `{ data: [] }` per call would be a new reference
 * every render, which is exactly the churn placeholderData exists to avoid.
 */
const EMPTY_SEARCH_PAGE: { data: Book[] } = { data: [] };

export const libraryQueries = {
  // ── Patrons ──
  /** Keyset list, filters ⊂ the key ⇒ the URL drives the cache. keepPreviousData across pages. */
  patrons: (filters: MemberListQuery) =>
    queryOptions({
      queryKey: libraryKeys.patrons.list(filters),
      queryFn: () => unwrap<KeysetPage<Member>>(libApi.patrons.$get({ query: filters })),
      staleTime: STALE.list,
      placeholderData: keepPreviousData,
    }),

  /**
   * The SAME roster as an INFINITE stream (2026-09-22 — the owner: "now let's
   * do it with every table and card listing of every portal"). Same endpoint,
   * same keyset, same projection as `patrons` above; the only difference is
   * that pages ACCUMULATE in one cache entry instead of replacing each other.
   *
   * Read by the roster (/library/patrons) and by the card register
   * (/library/patrons/cards), which derives one card per member from the same
   * rows. The filters are the key MINUS the cursor — the cursor is a position
   * inside the stream now, not part of its identity. NO `keepPreviousData`: a
   * filter change starts a NEW stream, and holding the old members under the
   * new controls would show a list that does not match them.
   */
  patronsInfinite: (filters: Omit<MemberListQuery, "cursor">) =>
    infiniteQueryOptions({
      queryKey: [...libraryKeys.patrons.list(filters), "infinite"] as const,
      queryFn: ({ pageParam }) =>
        unwrap<KeysetPage<Member>>(libApi.patrons.$get({ query: { ...filters, cursor: pageParam } })),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (last: KeysetPage<Member>) => last.nextCursor ?? undefined,
      staleTime: STALE.list,
    }),

  /** A single patron — the detail view + the edit form's defaultValues source. */
  patron: (id: string) =>
    queryOptions({
      queryKey: libraryKeys.patrons.detail(id),
      queryFn: () => unwrap<Member>(libApi.patrons[":id"].$get({ param: { id } })),
      staleTime: STALE.detail,
    }),

  /** Derived circulation counters — its own key so a profile edit doesn't refetch these and vice-versa. */
  patronAccount: (id: string) =>
    queryOptions({
      queryKey: libraryKeys.patrons.account(id),
      queryFn: () => unwrap<MemberAccount>(libApi.patrons[":id"].account.$get({ param: { id } })),
      staleTime: STALE.detail,
    }),

  /**
   * Patron groups — an UNPAGED policy registry (bare array; GET /patron-groups
   * ignores query). role + memberCount are DERIVED server-side (GROUP_ROLE map);
   * the POS reads these loan/fine verdicts. Reference tier (rarely edited).
   * Consumed by the PatronGroups screen AND the PatronForm role-policy preview.
   */
  patronGroups: () =>
    queryOptions({
      queryKey: libraryKeys.patrons.groups(),
      queryFn: () => unwrap<PatronGroup[]>(libApi["patron-groups"].$get()),
      staleTime: STALE.reference,
    }),

  /**
   * The library-card register's figures — GET /patrons/cards/stats. The card
   * state is DERIVED server-side from the member's join date and the resolved
   * group's validity, over EVERY member, so the tiles are the register and not
   * the page (the V-01 scar: four cards that counted the first 25 rows). Under
   * patrons.all(), so a suspend/reactivate moves them.
   */
  cardStats: () =>
    queryOptions({
      queryKey: libraryKeys.patrons.cardStats(),
      queryFn: () => unwrap<CardStats>(libApi.patrons.cards.stats.$get()),
      staleTime: STALE.list,
    }),

  // ── Catalogue ──
  /** Keyset list with server-side search+sort+facets — all live in BookListQuery, all in the key. */
  books: (filters: BookListQuery) =>
    queryOptions({
      queryKey: libraryKeys.catalogue.books.list(filters),
      queryFn: () => unwrap<KeysetPage<Book>>(libApi.books.$get({ query: filters })),
      staleTime: STALE.list,
      placeholderData: keepPreviousData,
    }),

  /**
   * The SAME catalogue browse as an INFINITE stream (2026-09-22 — the owner:
   * "rather than doing next page like limit/offset pagination we need infinite
   * scroll both in tables and cards"). Same endpoint, same keyset, same
   * projection as `books` above; the only difference is that pages ACCUMULATE
   * in one cache entry instead of replacing each other.
   *
   * · `initialPageParam: undefined` — the first page carries no cursor, exactly
   *   as the first `?cursor=`-less request always did.
   * · `getNextPageParam` reads the wire's own `nextCursor`; `null` (the last
   *   page) becomes `undefined`, which is how TanStack says "no more".
   * · The filters are the key, MINUS the cursor — the cursor is a position
   *   inside the stream now, not part of its identity, so it has left the URL
   *   and the key alike. `"infinite"` on the end keeps this entry distinct from
   *   the paged `books(filters)` one, which other screens still use.
   * · NO `keepPreviousData`: a filter or sort change starts a NEW stream, and
   *   holding the old titles under the new controls would show a list that does
   *   not match them. The table shows its skeleton rows instead.
   */
  booksInfinite: (filters: Omit<BookListQuery, "cursor">) =>
    infiniteQueryOptions({
      queryKey: [...libraryKeys.catalogue.books.list(filters), "infinite"] as const,
      queryFn: ({ pageParam }) =>
        unwrap<KeysetPage<Book>>(libApi.books.$get({ query: { ...filters, cursor: pageParam } })),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (last: KeysetPage<Book>) => last.nextCursor ?? undefined,
      staleTime: STALE.list,
    }),

  /** A single title with its full detail projection (copies summary, holds, etc.). */
  book: (id: string) =>
    queryOptions({
      queryKey: libraryKeys.catalogue.books.detail(id),
      queryFn: () => unwrap<BookDetail>(libApi.books[":id"].$get({ param: { id } })),
      staleTime: STALE.detail,
    }),

  /** FTS5 title search — a capped page (no cursor); the omnibox/typeahead source. */
  catalogueSearch: (query: CatalogueSearchQuery) =>
    queryOptions({
      queryKey: libraryKeys.catalogue.search(query),
      // The search endpoint returns a capped `{ data }` (no nextCursor — R2 caps q, not a keyset).
      queryFn: () => unwrap<{ data: Book[] }>(libApi.catalogue.search.$get({ query })),
      staleTime: STALE.list,
      // SEEDED, not bare keepPreviousData — and the seed is the whole point.
      //
      // solid-query's `.data` getter suspends while `state.data` is undefined
      // (build/index.js: `state.data !== undefined ? resource.latest : resource()`).
      // keepPreviousData has no previous page on the FIRST search, so the first
      // keystroke past the debounce suspended — and the boundary that caught it
      // was the PORTAL'S, wrapped around the whole <Outlet/>. Solid answers a
      // suspend by moving the subtree into an offscreen fragment, which blurs
      // whatever is focused inside it: the omnibox lost the caret on the first
      // letter and the page flashed a route skeleton. (2026-09-08, reported.)
      //
      // No boundary placed inside the page can fix that. `createResource` binds
      // to the Suspense in scope where useQuery is CALLED — the screen's own
      // body — so the boundary is always an ancestor of the input that started
      // the search. The query simply must not suspend.
      //
      // An empty first page is also the honest value: before the first response
      // there ARE no matches yet. `isFetching` still says a request is in
      // flight, which is what the table's loading state reads.
      placeholderData: (prev) => prev ?? EMPTY_SEARCH_PAGE,
    }),

  /** Header stat cards (titles / total copies / available / checked out) — its own key so a book write refreshes counts without touching a list page. */
  catalogueStats: () =>
    queryOptions({
      queryKey: libraryKeys.catalogue.stats(),
      queryFn: () => unwrap<CatalogueStats>(libApi.catalogue.stats.$get()),
      staleTime: STALE.list,
    }),

  /**
   * Bibliographic lookup for cataloguing. A published record does not change,
   * and the edge caches it too, so this is effectively immutable per query —
   * bulk-add re-looks-up the same ISBN across sessions without refetching.
   */
  catalogueLookup: (q: string) =>
    queryOptions({
      queryKey: libraryKeys.catalogue.lookup(q),
      queryFn: () => unwrap<CodeLookup>(libApi.catalogue.lookup.$get({ query: { q } })),
      staleTime: Infinity,
      enabled: q.trim().length > 0,
    }),

  /** Author authority lookup — same immutable-per-query semantics as the book lookup. */
  authorLookup: (q: string) =>
    queryOptions({
      queryKey: libraryKeys.catalogue.authorLookup(q),
      queryFn: () => unwrap<AuthorCodeLookup>(libApi.catalogue.lookup.author.$get({ query: { q } })),
      staleTime: Infinity,
      enabled: q.trim().length > 0,
    }),

  /** Publisher candidates by name — a BARE array of matches. */
  publisherLookup: (q: string) =>
    queryOptions({
      queryKey: libraryKeys.catalogue.publisherLookup(q),
      queryFn: () => unwrap<PublisherMatch[]>(libApi.catalogue.lookup.publisher.$get({ query: { q } })),
      staleTime: Infinity,
      enabled: q.trim().length > 0,
    }),

  /** A book's copies — a BARE array (not keyset); the detail screen's Copies panel source. */
  bookCopies: (id: string) =>
    queryOptions({
      queryKey: libraryKeys.catalogue.books.copies(id),
      queryFn: () => unwrap<Copy[]>(libApi.books[":id"].copies.$get({ param: { id } })),
      staleTime: STALE.detail,
    }),

  // ── Authority registries ──
  /** Keyset authors list (search = name|subject prefix). booksCount is live per row. */
  authors: (filters: AuthorListQuery) =>
    queryOptions({
      queryKey: libraryKeys.catalogue.authors.list(filters),
      queryFn: () => unwrap<KeysetPage<Author>>(libApi.authors.$get({ query: filters })),
      staleTime: STALE.list,
      placeholderData: keepPreviousData,
    }),

  /**
   * The authority registry as an INFINITE stream (2026-09-22). Same endpoint,
   * same keyset (name, id) — which is what makes it safe to accumulate: page two
   * continues the alphabet page one started. The search is the key MINUS the
   * cursor. NO `keepPreviousData`: a new search is a new stream, and the table
   * shows its bones rather than the previous search's rows.
   */
  authorsInfinite: (filters: Omit<AuthorListQuery, "cursor">) =>
    infiniteQueryOptions({
      queryKey: [...libraryKeys.catalogue.authors.list(filters), "infinite"] as const,
      queryFn: ({ pageParam }) =>
        unwrap<KeysetPage<Author>>(libApi.authors.$get({ query: { ...filters, cursor: pageParam } })),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (last: KeysetPage<Author>) => last.nextCursor ?? undefined,
      staleTime: STALE.list,
    }),

  /**
   * Typeahead slice of the AUTHOR registry — the catalogue's author facet.
   *
   * Rides the same keyset endpoint the registry screen uses, so there is no
   * second query surface to keep in step; it just asks for one capped page and
   * throws the cursor away. Reference tier: an authority list does not churn.
   */
  authorSearch: (search: string, limit = 20) =>
    queryOptions({
      queryKey: [...libraryKeys.catalogue.authors.all(), "typeahead", search, limit] as const,
      queryFn: () =>
        unwrap<KeysetPage<Author>>(
          libApi.authors.$get({ query: { search: search || undefined, limit: String(limit) } }),
        ),
      staleTime: STALE.reference,
    }),

  /**
   * Possible duplicate authors (§6) — clusters, with the evidence that put each
   * one together. Fetched only when the panel is open: it is a review tool, not
   * something the register pays for on every visit. `staleTime` is short because
   * a merge changes the answer, and the merge mutation already invalidates
   * authors.all(), which this key sits under.
   */
  authorDuplicates: (limit = 25, enabled = true) =>
    queryOptions({
      queryKey: libraryKeys.catalogue.authors.duplicates(limit),
      queryFn: () =>
        unwrap<AuthorDuplicatesResult>(libApi.authors.duplicates.$get({ query: { limit: String(limit) } })),
      staleTime: STALE.list,
      enabled,
    }),

  /** A single author — the detail view + the edit modal's defaultValues source. */
  author: (id: string) =>
    queryOptions({
      queryKey: libraryKeys.catalogue.authors.detail(id),
      queryFn: () => unwrap<Author>(libApi.authors[":id"].$get({ param: { id } })),
      staleTime: STALE.detail,
    }),

  /** Keyset publishers list (search = name prefix). */
  publishers: (filters: PublisherListQuery) =>
    queryOptions({
      queryKey: libraryKeys.catalogue.publishers.list(filters),
      queryFn: () => unwrap<KeysetPage<Publisher>>(libApi.publishers.$get({ query: filters })),
      staleTime: STALE.list,
      placeholderData: keepPreviousData,
    }),

  /** The imprint registry as an INFINITE stream (2026-09-22) — same endpoint, same keyset (name, id); the search is the key, the cursor is not. See `authorsInfinite`. */
  publishersInfinite: (filters: Omit<PublisherListQuery, "cursor">) =>
    infiniteQueryOptions({
      queryKey: [...libraryKeys.catalogue.publishers.list(filters), "infinite"] as const,
      queryFn: ({ pageParam }) =>
        unwrap<KeysetPage<Publisher>>(libApi.publishers.$get({ query: { ...filters, cursor: pageParam } })),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (last: KeysetPage<Publisher>) => last.nextCursor ?? undefined,
      staleTime: STALE.list,
    }),

  /** Typeahead slice of the PUBLISHER registry — see `authorSearch`. */
  publisherSearch: (search: string, limit = 20) =>
    queryOptions({
      queryKey: [...libraryKeys.catalogue.publishers.all(), "typeahead", search, limit] as const,
      queryFn: () =>
        unwrap<KeysetPage<Publisher>>(
          libApi.publishers.$get({ query: { search: search || undefined, limit: String(limit) } }),
        ),
      staleTime: STALE.reference,
    }),

  /** A single publisher. */
  publisher: (id: string) =>
    queryOptions({
      queryKey: libraryKeys.catalogue.publishers.detail(id),
      queryFn: () => unwrap<Publisher>(libApi.publishers[":id"].$get({ param: { id } })),
      staleTime: STALE.detail,
    }),

  /** Subjects — an UNPAGED registry (bare array). Reference tier (rarely edited). */
  subjects: () =>
    queryOptions({
      queryKey: libraryKeys.catalogue.subjects(),
      queryFn: () => unwrap<Subject[]>(libApi.subjects.$get()),
      staleTime: STALE.reference,
    }),

  /**
   * Typeahead slice of the subject registry — `search` prefix-filters server
   * side and `limit` caps the response, so a picker never pulls a registry
   * that has grown to thousands of rows. Its own cache key per query; the
   * unparameterised `subjects()` above is untouched and still serves the
   * registry screens that want the whole list.
   */
  subjectSearch: (search: string, limit = 20) =>
    queryOptions({
      queryKey: [...libraryKeys.catalogue.subjects(), "search", search, limit] as const,
      queryFn: () =>
        unwrap<Subject[]>(libApi.subjects.$get({ query: { search: search || undefined, limit: String(limit) } })),
      staleTime: STALE.reference,
    }),

  /** Tags — an UNPAGED registry (bare array). Reference tier. */
  tags: () =>
    queryOptions({
      queryKey: libraryKeys.catalogue.tags(),
      queryFn: () => unwrap<Tag[]>(libApi.tags.$get()),
      staleTime: STALE.reference,
    }),

  // ── Locations (copy placement — libraries → branches → shelves) ──
  /** Libraries — an UNPAGED registry (bare array). Reference tier. */
  libraries: () =>
    queryOptions({
      queryKey: libraryKeys.locations.libraries(),
      queryFn: () => unwrap<Library[]>(libApi.libraries.$get()),
      staleTime: STALE.reference,
    }),

  /** Typeahead slice of the library registry — see `subjectSearch`. */
  librarySearch: (search: string, limit = 20) =>
    queryOptions({
      queryKey: [...libraryKeys.locations.libraries(), "search", search, limit] as const,
      queryFn: () =>
        unwrap<Library[]>(libApi.libraries.$get({ query: { search: search || undefined, limit: String(limit) } })),
      staleTime: STALE.reference,
    }),

  /** Branches, optionally filtered by library — a bare array keyed by the parent id. */
  branches: (libraryId?: string) =>
    queryOptions({
      queryKey: libraryKeys.locations.branches(libraryId),
      queryFn: () =>
        unwrap<Branch[]>(libApi.branches.$get({ query: libraryId ? { libraryId } : {} })),
      staleTime: STALE.reference,
    }),

  /** A single library — the MyLibraries detail view (GET /libraries/:id → the bridge-aware header). */
  library: (id: string) =>
    queryOptions({
      queryKey: libraryKeys.locations.library(id),
      queryFn: () => unwrap<Library>(libApi.libraries[":id"].$get({ param: { id } })),
      staleTime: STALE.detail,
    }),

  /** Zones, optionally filtered by branch — a bare array keyed by the parent id. */
  zones: (branchId?: string) =>
    queryOptions({
      queryKey: libraryKeys.locations.zones(branchId),
      queryFn: () => unwrap<Zone[]>(libApi.zones.$get({ query: branchId ? { branchId } : {} })),
      staleTime: STALE.reference,
    }),

  /** Stacks, optionally filtered by zone — a bare array keyed by the parent id. */
  stacks: (zoneId?: string) =>
    queryOptions({
      queryKey: libraryKeys.locations.stacks(zoneId),
      queryFn: () => unwrap<Stack[]>(libApi.stacks.$get({ query: zoneId ? { zoneId } : {} })),
      staleTime: STALE.reference,
    }),

  /** Shelves, optionally filtered by stack — a bare array keyed by the parent id. */
  shelves: (stackId?: string) =>
    queryOptions({
      queryKey: libraryKeys.locations.shelves(stackId),
      queryFn: () => unwrap<Shelf[]>(libApi.shelves.$get({ query: stackId ? { stackId } : {} })),
      staleTime: STALE.reference,
    }),

  /** The shelf-list printout rows (call-number order; lib:catalog.read). */
  shelfHoldings: (id: string) =>
    queryOptions({
      queryKey: libraryKeys.locations.shelfHoldings(id),
      queryFn: () => {
        const args = { param: { id } };
        return unwrap<{ barcode: string; status: string; title: string; author: string; isbn: string; classification: string | null }[]>(
          libApi.shelves[":id"].copies.$get(args),
        );
      },
      staleTime: STALE.list,
    }),

  // ── Circulation (the POS core) ──
  /**
   * Desk barcode lookup — near-live tier; the copy's state (on loan? on hold?)
   * must be fresh at the desk. `intent` matters only when the scanned code is
   * an ISBN: 'return' resolves the copy that is OUT and refuses an ambiguous
   * title, where 'issue' takes any free copy (see lookupCopy). It is part of
   * the KEY, so the two desks never serve each other's cached answer.
   */
  circulationLookup: (barcode: string, intent: "issue" | "return" = "issue") =>
    queryOptions({
      // intent is part of the KEY, so the two desks never serve each other's
      // cached answer for the same scanned code.
      queryKey: [...libraryKeys.circulation.lookup(barcode), intent],
      queryFn: () =>
        intent === "return"
          ? unwrap<CopyLookup>(libApi.circulation["return-lookup"][":barcode"].$get({ param: { barcode } }))
          : unwrap<CopyLookup>(libApi.circulation.lookup[":barcode"].$get({ param: { barcode } })),
      staleTime: STALE.lookup,
    }),

  /** The circulation ledger — keyset loans list (TransactionHistory). */
  loans: (filters: LoanListQuery) =>
    queryOptions({
      queryKey: libraryKeys.loans.list(filters),
      queryFn: () => unwrap<KeysetPage<Loan>>(libApi.circulation.loans.$get({ query: filters })),
      staleTime: STALE.list,
      placeholderData: keepPreviousData,
    }),

  /**
   * The circulation ledger as an INFINITE stream (2026-09-22) — the desk's
   * renewals register reads it. Same endpoint, same keyset, same projection as
   * `loans`; the filters are the key MINUS the cursor, and pages accumulate in
   * one entry. NO `keepPreviousData` — a status or search change is a new
   * stream, and the table shows its bones rather than the previous stage's rows.
   */
  loansInfinite: (filters: Omit<LoanListQuery, "cursor">) =>
    infiniteQueryOptions({
      queryKey: [...libraryKeys.loans.list(filters), "infinite"] as const,
      queryFn: ({ pageParam }) =>
        unwrap<KeysetPage<Loan>>(libApi.circulation.loans.$get({ query: { ...filters, cursor: pageParam } })),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (last: KeysetPage<Loan>) => last.nextCursor ?? undefined,
      staleTime: STALE.list,
    }),

  // ── Holds ──
  holds: (filters: HoldListQuery) =>
    queryOptions({
      queryKey: libraryKeys.holds.list(filters),
      queryFn: () => unwrap<KeysetPage<Hold>>(libApi.holds.$get({ query: filters })),
      staleTime: STALE.list,
      placeholderData: keepPreviousData,
    }),

  /**
   * The hold QUEUE as an INFINITE stream (2026-09-22). Same endpoint, same
   * keyset (queue_position ASC, id) — which is what makes this safe to
   * accumulate: the desk works the queue in order, so page two continues the
   * order page one started. The filters are the key MINUS the cursor.
   */
  holdsInfinite: (filters: Omit<HoldListQuery, "cursor">) =>
    infiniteQueryOptions({
      queryKey: [...libraryKeys.holds.list(filters), "infinite"] as const,
      queryFn: ({ pageParam }) =>
        unwrap<KeysetPage<Hold>>(libApi.holds.$get({ query: { ...filters, cursor: pageParam } })),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (last: KeysetPage<Hold>) => last.nextCursor ?? undefined,
      staleTime: STALE.list,
    }),

  /**
   * The queue's figures — GET /holds/stats, an institution-wide count by stage,
   * never the rows on a page (GET /holds is keyset). Under holds.all(), so every
   * transition and the return-path promotion refresh the tiles with the list.
   */
  holdStats: () =>
    queryOptions({
      queryKey: libraryKeys.holds.stats(),
      queryFn: () => unwrap<HoldStats>(libApi.holds.stats.$get()),
      staleTime: STALE.list,
    }),

  // ── Fines ──
  fines: (filters: FineListQuery) =>
    queryOptions({
      queryKey: libraryKeys.fines.list(filters),
      queryFn: () => unwrap<KeysetPage<Fine>>(libApi.fines.$get({ query: filters })),
      staleTime: STALE.list,
      placeholderData: keepPreviousData,
    }),

  /**
   * The fines ledger as an INFINITE stream (2026-09-22). Same endpoint, same
   * keyset, same projection as `fines`; the filters are the key MINUS the
   * cursor, and pages accumulate in one entry.
   */
  finesInfinite: (filters: Omit<FineListQuery, "cursor">) =>
    infiniteQueryOptions({
      queryKey: [...libraryKeys.fines.list(filters), "infinite"] as const,
      queryFn: ({ pageParam }) =>
        unwrap<KeysetPage<Fine>>(libApi.fines.$get({ query: { ...filters, cursor: pageParam } })),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (last: KeysetPage<Fine>) => last.nextCursor ?? undefined,
      staleTime: STALE.list,
    }),

  // ── OPAC · public catalogue ─────────────────────────────────────────────────
  // Anonymous-CAPABLE reads: the server's opacRead guard allows anonymous IFF
  // publicBrowse is on, else it requires lib:opac / lib:catalog.read. So a 401/403
  // here is a LEGIBLE "sign in to browse" state the screen maps by isAuthError —
  // NOT a bug (that gate decision is the server's, never the client's). These live
  // on their OWN opac.* keys (never the staff catalogue.*): a member browse and a
  // staff list are different projections behind different gates, so they must not
  // collide in the cache. Ebooks stay METADATA-only (the licensed binary rides the
  // entitlement-gated /me download-url, never the OPAC read).

  /** Branding + the publicBrowse flag — PUBLIC (unauth): the shell reads this before any sign-in. Reference tier (a toggle, rarely flipped). */
  opacSettings: () =>
    queryOptions({
      queryKey: libraryKeys.opac.settings(),
      queryFn: () => unwrap<OpacSettings>(libApi.opac.settings.$get()),
      staleTime: STALE.reference,
    }),

  /** OPAC FTS title search — the same capped `{ data }` (no cursor) as the staff omnibox, behind the public gate. */
  opacSearch: (query: CatalogueSearchQuery) =>
    queryOptions({
      queryKey: libraryKeys.opac.search(query),
      queryFn: () => unwrap<{ data: Book[] }>(libApi.opac.search.$get({ query })),
      staleTime: STALE.list,
      placeholderData: keepPreviousData,
    }),

  /** OPAC keyset book browse — bib + availability projection (no staff-only fields), filters ⊂ the key. */
  opacBooks: (filters: BookListQuery) =>
    queryOptions({
      queryKey: libraryKeys.opac.books.list(filters),
      queryFn: () => unwrap<KeysetPage<Book>>(libApi.opac.books.$get({ query: filters })),
      staleTime: STALE.list,
      placeholderData: keepPreviousData,
    }),

  /**
   * The OPAC browse as an INFINITE stream (2026-09-22 — the owner: "rather than
   * doing next page like limit/offset pagination we need infinite scroll").
   * Same endpoint, same keyset, same projection as `opacBooks` above: the only
   * difference is that pages ACCUMULATE in one cache entry instead of replacing
   * each other.
   *
   * · `initialPageParam: undefined` — the first page carries no cursor, exactly
   *   as the first `?cursor=`-less request always did.
   * · `getNextPageParam` reads the wire's own `nextCursor`; `null` (the last
   *   page) becomes `undefined`, which is how TanStack says "no more".
   * · The filters are the key, MINUS the cursor — the cursor is now a position
   *   inside the stream, not part of its identity, so it has left the URL and
   *   the key alike. `"infinite"` on the end keeps this entry distinct from the
   *   paged `opacBooks(filters)` one, which the landing's strips still use.
   * · NO `keepPreviousData`: a filter change starts a NEW stream, and holding
   *   the old titles under the new filters would show the reader a list that
   *   does not match the controls. The screen shows its skeleton cards instead.
   */
  opacBooksInfinite: (filters: Omit<BookListQuery, "cursor">) =>
    infiniteQueryOptions({
      queryKey: [...libraryKeys.opac.books.list(filters), "infinite"] as const,
      queryFn: ({ pageParam }) =>
        unwrap<KeysetPage<Book>>(libApi.opac.books.$get({ query: { ...filters, cursor: pageParam } })),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (last: KeysetPage<Book>) => last.nextCursor ?? undefined,
      staleTime: STALE.list,
    }),

  /**
   * The subject registry with its live title counts, behind the public gate —
   * the landing's "Browse by subject" chips (2026-09-22). Reference tier: the
   * registry changes when a cataloguer adds a subject, not with every loan.
   */
  opacSubjects: () =>
    queryOptions({
      queryKey: libraryKeys.opac.subjects(),
      queryFn: () => unwrap<Subject[]>(libApi.opac.subjects.$get()),
      staleTime: STALE.reference,
    }),

  /** A single public title (BookDetail — copies summary + availability). */
  opacBookDetail: (id: string) =>
    queryOptions({
      queryKey: libraryKeys.opac.books.detail(id),
      queryFn: () => unwrap<BookDetail>(libApi.opac.books[":id"].$get({ param: { id } })),
      staleTime: STALE.detail,
    }),

  /**
   * OPAC keyset ebook browse — METADATA only; the entitlement-gated binary is a
   * /me concern. Still the PAGED entry: the landing's "On the digital shelf"
   * strip reads exactly four of these and never pages, so it wants one page,
   * not a stream.
   */
  opacEbooks: (filters: EbookListQuery) =>
    queryOptions({
      queryKey: libraryKeys.opac.ebooks.list(filters),
      queryFn: () => unwrap<KeysetPage<Ebook>>(libApi.opac.ebooks.$get({ query: filters })),
      staleTime: STALE.list,
      placeholderData: keepPreviousData,
    }),

  /**
   * The digital shelf as an INFINITE stream (2026-09-22) — the same conversion
   * `opacBooksInfinite` above took, for the same reason and against the same
   * endpoint. Same keyset, same metadata-only projection: the only difference
   * is that pages ACCUMULATE in one cache entry instead of replacing each other.
   *
   * · `initialPageParam: undefined` — the first page carries no cursor, exactly
   *   as the first `?cursor=`-less request always did.
   * · `getNextPageParam` reads the wire's own `nextCursor`; `null` (the last
   *   page) becomes `undefined`, which is how TanStack says "no more".
   * · The filter is the key, MINUS the cursor — the cursor is a position inside
   *   the stream now, not part of its identity, so it has left the URL and the
   *   key alike. `"infinite"` on the end keeps this entry distinct from the
   *   paged `opacEbooks(filters)` one the landing's strip still uses.
   * · NO `keepPreviousData`: changing the format starts a NEW stream, and
   *   holding the old titles under the new filter would show the reader a shelf
   *   that does not match the control. The screen paints its bones instead.
   */
  opacEbooksInfinite: (filters: Omit<EbookListQuery, "cursor">) =>
    infiniteQueryOptions({
      queryKey: [...libraryKeys.opac.ebooks.list(filters), "infinite"] as const,
      queryFn: ({ pageParam }) =>
        unwrap<KeysetPage<Ebook>>(libApi.opac.ebooks.$get({ query: { ...filters, cursor: pageParam } })),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (last: KeysetPage<Ebook>) => last.nextCursor ?? undefined,
      staleTime: STALE.list,
    }),

  // ── /me · the signed-in member's self-service ───────────────────────────────
  // rbac('lib:opac'); the member is SERVER-derived from the session, so these
  // carry NO id (the anti-IDOR spine). An authenticated-but-UNLINKED session reads
  // legible empties — { member: null } / zero counters / empty pages — never a 404
  // or a throw, so the account shell renders a link-your-account CTA over blanks.

  /** The resolved membership for the session (or { member: null } — a 200 CTA state, never a 404). */
  me: () =>
    queryOptions({
      queryKey: libraryKeys.me.membership(),
      queryFn: () => unwrap<MeResponse>(libApi.me.$get()),
      staleTime: STALE.detail,
    }),

  /** Derived circulation counters for the member — its own key so a hold/renew refreshes counters without refetching the profile. */
  meAccount: () =>
    queryOptions({
      queryKey: libraryKeys.me.account(),
      queryFn: () => unwrap<MyAccount>(libApi.me.account.$get()),
      staleTime: STALE.detail,
    }),

  /** The member's loans — keyset, server-forced to the member_id (query is CursorQuery + status only). */
  meLoans: (filters: MyLoanListQuery) =>
    queryOptions({
      queryKey: libraryKeys.me.loans(filters),
      queryFn: () => unwrap<KeysetPage<Loan>>(libApi.me.loans.$get({ query: filters })),
      staleTime: STALE.list,
      placeholderData: keepPreviousData,
    }),

  /** The member's holds — keyset, server-forced to the member_id. */
  meHolds: (filters: MyHoldListQuery) =>
    queryOptions({
      queryKey: libraryKeys.me.holds(filters),
      queryFn: () => unwrap<KeysetPage<Hold>>(libApi.me.holds.$get({ query: filters })),
      staleTime: STALE.list,
      placeholderData: keepPreviousData,
    }),

  /** The member's fines — keyset, server-forced to the member_id. */
  meFines: (filters: MyFineListQuery) =>
    queryOptions({
      queryKey: libraryKeys.me.fines(filters),
      queryFn: () => unwrap<KeysetPage<Fine>>(libApi.me.fines.$get({ query: filters })),
      staleTime: STALE.list,
      placeholderData: keepPreviousData,
    }),

  /* The member's three account registers as INFINITE streams (2026-09-22 — the
     owner: "now let's do it with every table and card listing of every
     portal"). Same three endpoints, same keysets, same projections as the
     paged factories above; the status filter is the key MINUS the cursor, and
     pages accumulate in one entry each. NO `keepPreviousData` — a status
     change is a new stream, back to page one. */

  /** The member's loans, accumulating. */
  meLoansInfinite: (filters: Omit<MyLoanListQuery, "cursor">) =>
    infiniteQueryOptions({
      queryKey: [...libraryKeys.me.loans(filters), "infinite"] as const,
      queryFn: ({ pageParam }) =>
        unwrap<KeysetPage<Loan>>(libApi.me.loans.$get({ query: { ...filters, cursor: pageParam } })),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (last: KeysetPage<Loan>) => last.nextCursor ?? undefined,
      staleTime: STALE.list,
    }),

  /** The member's holds, accumulating. */
  meHoldsInfinite: (filters: Omit<MyHoldListQuery, "cursor">) =>
    infiniteQueryOptions({
      queryKey: [...libraryKeys.me.holds(filters), "infinite"] as const,
      queryFn: ({ pageParam }) =>
        unwrap<KeysetPage<Hold>>(libApi.me.holds.$get({ query: { ...filters, cursor: pageParam } })),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (last: KeysetPage<Hold>) => last.nextCursor ?? undefined,
      staleTime: STALE.list,
    }),

  /** The member's fines, accumulating. */
  meFinesInfinite: (filters: Omit<MyFineListQuery, "cursor">) =>
    infiniteQueryOptions({
      queryKey: [...libraryKeys.me.fines(filters), "infinite"] as const,
      queryFn: ({ pageParam }) =>
        unwrap<KeysetPage<Fine>>(libApi.me.fines.$get({ query: { ...filters, cursor: pageParam } })),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (last: KeysetPage<Fine>) => last.nextCursor ?? undefined,
      staleTime: STALE.list,
    }),

  // ══ L5 · Digital (ebooks + reading sessions + storage meters) ═══════════════
  /** Keyset ebook catalogue — filters (search/format/license/status/library) ⊂ the key. */
  ebooks: (filters: EbookListQuery) =>
    queryOptions({
      queryKey: libraryKeys.digital.ebooks.list(filters),
      queryFn: () => unwrap<KeysetPage<Ebook>>(libApi.digital.ebooks.$get({ query: filters })),
      staleTime: STALE.list,
      placeholderData: keepPreviousData,
    }),

  /** The ebook catalogue as an INFINITE stream (2026-09-22) — same endpoint, same keyset; the search + the four facets are the key, the cursor is not. See `booksInfinite`. */
  ebooksInfinite: (filters: Omit<EbookListQuery, "cursor">) =>
    infiniteQueryOptions({
      queryKey: [...libraryKeys.digital.ebooks.list(filters), "infinite"] as const,
      queryFn: ({ pageParam }) =>
        unwrap<KeysetPage<Ebook>>(libApi.digital.ebooks.$get({ query: { ...filters, cursor: pageParam } })),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (last: KeysetPage<Ebook>) => last.nextCursor ?? undefined,
      staleTime: STALE.list,
    }),

  /** A single ebook — METADATA only (the licensed binary rides the access-gated download-url helper). */
  ebook: (id: string) =>
    queryOptions({
      queryKey: libraryKeys.digital.ebooks.detail(id),
      queryFn: () => unwrap<Ebook>(libApi.digital.ebooks[":id"].$get({ param: { id } })),
      staleTime: STALE.detail,
    }),

  /** Keyset reading-sessions log (analytics; lib:reports at the API). ?active/?device ⊂ the key. */
  readingSessions: (filters: SessionListQuery) =>
    queryOptions({
      queryKey: libraryKeys.digital.sessions(filters),
      queryFn: () => unwrap<KeysetPage<ReadingSession>>(libApi.digital.sessions.$get({ query: filters })),
      staleTime: STALE.list,
      placeholderData: keepPreviousData,
    }),

  /** The reading-sessions log as an INFINITE stream (2026-09-22) — same endpoint, same keyset (newest first); ?active/?device ⊂ the key, the cursor is not. */
  readingSessionsInfinite: (filters: Omit<SessionListQuery, "cursor">) =>
    infiniteQueryOptions({
      queryKey: [...libraryKeys.digital.sessions(filters), "infinite"] as const,
      queryFn: ({ pageParam }) =>
        unwrap<KeysetPage<ReadingSession>>(libApi.digital.sessions.$get({ query: { ...filters, cursor: pageParam } })),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (last: KeysetPage<ReadingSession>) => last.nextCursor ?? undefined,
      staleTime: STALE.list,
    }),

  /** R2 storage + bandwidth meters (derived; lib:reports at the API). Its own leaf — an ebook write refreshes it. */
  storageUsage: () =>
    queryOptions({
      queryKey: libraryKeys.digital.storage(),
      queryFn: () => unwrap<StorageUsage>(libApi.digital.storage.$get()),
      staleTime: STALE.list,
    }),

  /**
   * ONE display asset's INLINE presigned GET by stored key (cover/image
   * thumbnails — <img src> renders it; ebook-kind keys 404/400 server-side).
   * STALE.asset (240s) sits under the 300s presign TTL so a cache hit can
   * never hand out an expired URL.
   */
  assetUrl: (key: string) =>
    queryOptions({
      queryKey: libraryKeys.digital.assetUrl(key),
      queryFn: () => unwrap<PresignedGet>(libApi.digital.uploads["download-url"].$get({ query: { key } })),
      staleTime: STALE.asset,
    }),

  /**
   * A table page's display-asset URLs in ONE round-trip (POST /catalogue/
   * asset-urls — kills the per-row presign N+1). The factory sorts + dedupes
   * so the key (and the wire body) are order-insensitive; unknown/pending keys
   * are simply absent from `urls` — render the placeholder, never an error
   * state. Same TTL-bounded staleTime as assetUrl.
   */
  assetUrls: (keys: string[]) => {
    const sorted = [...new Set(keys)].sort();
    return queryOptions({
      queryKey: libraryKeys.digital.assetUrls(sorted),
      queryFn: () => unwrap<BatchAssetUrlsResult>(libApi.catalogue["asset-urls"].$post({ json: { keys: sorted } })),
      staleTime: STALE.asset,
      enabled: sorted.length > 0, // the body law is min(1) — an empty page just doesn't ask
    });
  },

  // ══ L6 · Serials (subscriptions + the Kardex issue log) ═════════════════════
  /** Keyset serials list (fixed ORDER BY title,id; no filters — source parity). */
  serials: (filters: SerialListQuery) =>
    queryOptions({
      queryKey: libraryKeys.serials.list(filters),
      queryFn: () => unwrap<KeysetPage<Serial>>(libApi.catalogue.serials.$get({ query: filters })),
      staleTime: STALE.list,
      placeholderData: keepPreviousData,
    }),

  /**
   * The subscription register as an INFINITE stream (2026-09-22). Same endpoint,
   * same keyset (title, id) — which is what makes it safe to accumulate: page
   * two continues the alphabet page one started. The filters are the key MINUS
   * the cursor. NO `keepPreviousData`: a new search is a new stream, and the
   * table shows its bones rather than the previous search's rows.
   */
  serialsInfinite: (filters: Omit<SerialListQuery, "cursor">) =>
    infiniteQueryOptions({
      queryKey: [...libraryKeys.serials.list(filters), "infinite"] as const,
      queryFn: ({ pageParam }) =>
        unwrap<KeysetPage<Serial>>(libApi.catalogue.serials.$get({ query: { ...filters, cursor: pageParam } })),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (last: KeysetPage<Serial>) => last.nextCursor ?? undefined,
      staleTime: STALE.list,
    }),

  /** The subscription register's figures — GET /catalogue/serials/stats (statuses, the SUM of open claims, and how many issues are late). */
  serialStats: () =>
    queryOptions({
      queryKey: libraryKeys.serials.stats(),
      queryFn: () => unwrap<SerialStats>(libApi.catalogue.serials.stats.$get()),
      staleTime: STALE.list,
    }),

  /** A single subscription — the detail header the Kardex panel hangs off. */
  serial: (id: string) =>
    queryOptions({
      queryKey: libraryKeys.serials.detail(id),
      queryFn: () => unwrap<Serial>(libApi.catalogue.serials[":id"].$get({ param: { id } })),
      staleTime: STALE.detail,
    }),

  /** A subscription's Kardex — keyset issues scoped to the parent id (param + query). */
  serialIssues: (id: string, filters: SerialIssueListQuery) =>
    queryOptions({
      queryKey: libraryKeys.serials.issues(id, filters),
      queryFn: () => {
        // Param route whose query is validated INSIDE the handler → hc types the
        // input as `{ param }` only. Bind args to a const so the typed `query`
        // threads through (the mutations const-escape, applied to a GET).
        const args = { param: { id }, query: filters };
        return unwrap<KeysetPage<SerialIssue>>(libApi.catalogue.serials[":id"].issues.$get(args));
      },
      staleTime: STALE.list,
      placeholderData: keepPreviousData,
    }),

  /** A subscription's Kardex as an INFINITE stream (2026-09-22) — same endpoint, same keyset, scoped to the parent id; the key is the parent plus the filters MINUS the cursor. */
  serialIssuesInfinite: (id: string, filters: Omit<SerialIssueListQuery, "cursor">) =>
    infiniteQueryOptions({
      queryKey: [...libraryKeys.serials.issues(id, filters), "infinite"] as const,
      queryFn: ({ pageParam }) => {
        const args = { param: { id }, query: { ...filters, cursor: pageParam } }; // param+query const-escape (see serialIssues)
        return unwrap<KeysetPage<SerialIssue>>(libApi.catalogue.serials[":id"].issues.$get(args));
      },
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (last: KeysetPage<SerialIssue>) => last.nextCursor ?? undefined,
      staleTime: STALE.list,
    }),

  // ══ L6 · Inter-library transit (the desk ship/receive log) ══════════════════
  /** Keyset transit log — ?status/?direction ⊂ the key. */
  transits: (filters: TransitListQuery) =>
    queryOptions({
      queryKey: libraryKeys.transit.list(filters),
      queryFn: () => unwrap<KeysetPage<Transit>>(libApi.circulation.transit.$get({ query: filters })),
      staleTime: STALE.list,
      placeholderData: keepPreviousData,
    }),

  /** The transit log as an INFINITE stream (2026-09-22) — same endpoint, same keyset (newest first); ?status/?direction ⊂ the key, the cursor is not. */
  transitsInfinite: (filters: Omit<TransitListQuery, "cursor">) =>
    infiniteQueryOptions({
      queryKey: [...libraryKeys.transit.list(filters), "infinite"] as const,
      queryFn: ({ pageParam }) =>
        unwrap<KeysetPage<Transit>>(libApi.circulation.transit.$get({ query: { ...filters, cursor: pageParam } })),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (last: KeysetPage<Transit>) => last.nextCursor ?? undefined,
      staleTime: STALE.list,
    }),

  /** The transfer log's figures — GET /circulation/transit/stats, by stage across both directions. */
  transitStats: () =>
    queryOptions({
      queryKey: libraryKeys.transit.stats(),
      queryFn: () => unwrap<TransitStats>(libApi.circulation.transit.stats.$get()),
      staleTime: STALE.list,
    }),

  // ══ L6 · Stocktake / audit-reconciliation (session → scan → alert) ══════════
  /** Keyset audit-session list — ?status ⊂ the key. */
  auditSessions: (filters: AuditSessionListQuery) =>
    queryOptions({
      queryKey: libraryKeys.stocktake.sessions(filters),
      queryFn: () => unwrap<KeysetPage<AuditSession>>(libApi.stocktake.sessions.$get({ query: filters })),
      staleTime: STALE.list,
      placeholderData: keepPreviousData,
    }),

  /** The stocktake register as an INFINITE stream (2026-09-22) — same endpoint, same keyset; ?status ⊂ the key, the cursor is not. */
  auditSessionsInfinite: (filters: Omit<AuditSessionListQuery, "cursor">) =>
    infiniteQueryOptions({
      queryKey: [...libraryKeys.stocktake.sessions(filters), "infinite"] as const,
      queryFn: ({ pageParam }) =>
        unwrap<KeysetPage<AuditSession>>(libApi.stocktake.sessions.$get({ query: { ...filters, cursor: pageParam } })),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (last: KeysetPage<AuditSession>) => last.nextCursor ?? undefined,
      staleTime: STALE.list,
    }),

  /** The stocktake register's figures — GET /stocktake/sessions/stats: sessions by status plus the scans and still-open alerts under ALL of them. */
  stocktakeStats: () =>
    queryOptions({
      queryKey: libraryKeys.stocktake.stats(),
      queryFn: () => unwrap<StocktakeStats>(libApi.stocktake.sessions.stats.$get()),
      staleTime: STALE.list,
    }),

  /** A single audit session — the detail header the scans/alerts panels hang off. */
  auditSession: (id: string) =>
    queryOptions({
      queryKey: libraryKeys.stocktake.session(id),
      queryFn: () => unwrap<AuditSession>(libApi.stocktake.sessions[":id"].$get({ param: { id } })),
      staleTime: STALE.detail,
    }),

  /** A session's scans — keyset scoped to the parent id (param + query). */
  stocktakeScans: (id: string, filters: StocktakeScanListQuery) =>
    queryOptions({
      queryKey: libraryKeys.stocktake.scans(id, filters),
      queryFn: () => {
        const args = { param: { id }, query: filters }; // param+query const-escape (see serialIssues)
        return unwrap<KeysetPage<StocktakeScan>>(libApi.stocktake.sessions[":id"].scans.$get(args));
      },
      staleTime: STALE.list,
      placeholderData: keepPreviousData,
    }),

  /** A session's scans as an INFINITE stream (2026-09-22) — the sighting log accumulates as the counter scrolls it, which is the order it was recorded in. */
  stocktakeScansInfinite: (id: string, filters: Omit<StocktakeScanListQuery, "cursor">) =>
    infiniteQueryOptions({
      queryKey: [...libraryKeys.stocktake.scans(id, filters), "infinite"] as const,
      queryFn: ({ pageParam }) => {
        const args = { param: { id }, query: { ...filters, cursor: pageParam } }; // param+query const-escape (see serialIssues)
        return unwrap<KeysetPage<StocktakeScan>>(libApi.stocktake.sessions[":id"].scans.$get(args));
      },
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (last: KeysetPage<StocktakeScan>) => last.nextCursor ?? undefined,
      staleTime: STALE.list,
    }),

  /** A session's reconciliation alerts — keyset scoped to the parent id; ?resolved ⊂ the key. */
  auditAlerts: (id: string, filters: AuditAlertListQuery) =>
    queryOptions({
      queryKey: libraryKeys.stocktake.alerts(id, filters),
      queryFn: () => {
        const args = { param: { id }, query: filters }; // param+query const-escape (see serialIssues)
        return unwrap<KeysetPage<AuditAlert>>(libApi.stocktake.sessions[":id"].alerts.$get(args));
      },
      staleTime: STALE.list,
      placeholderData: keepPreviousData,
    }),

  /** A session's reconciliation alerts as an INFINITE stream (2026-09-22) — ?resolved ⊂ the key (the open/resolved/all switch re-keys it), the cursor is not. */
  auditAlertsInfinite: (id: string, filters: Omit<AuditAlertListQuery, "cursor">) =>
    infiniteQueryOptions({
      queryKey: [...libraryKeys.stocktake.alerts(id, filters), "infinite"] as const,
      queryFn: ({ pageParam }) => {
        const args = { param: { id }, query: { ...filters, cursor: pageParam } }; // param+query const-escape (see serialIssues)
        return unwrap<KeysetPage<AuditAlert>>(libApi.stocktake.sessions[":id"].alerts.$get(args));
      },
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (last: KeysetPage<AuditAlert>) => last.nextCursor ?? undefined,
      staleTime: STALE.list,
    }),

  // ══ L7 · Acquisitions (vendors / budget lines / POs / receiving) ════════════
  // NB: vendor + budget-line DETAIL are client-side (find-in-list — no GET :id on
  // the API surface, the Subjects registry precedent). Only POs expose a :id read.
  /** Keyset vendors list. */
  vendors: (filters: VendorListQuery) =>
    queryOptions({
      queryKey: libraryKeys.acquisitions.vendors(filters),
      queryFn: () => unwrap<KeysetPage<Vendor>>(libApi.acquisitions.vendors.$get({ query: filters })),
      staleTime: STALE.list,
      placeholderData: keepPreviousData,
    }),

  /** The vendor register as an INFINITE stream (2026-09-22) — same endpoint, same keyset; VendorListQuery is a bare cursor list, so the key is the limit alone. */
  vendorsInfinite: (filters: Omit<VendorListQuery, "cursor">) =>
    infiniteQueryOptions({
      queryKey: [...libraryKeys.acquisitions.vendors(filters), "infinite"] as const,
      queryFn: ({ pageParam }) =>
        unwrap<KeysetPage<Vendor>>(libApi.acquisitions.vendors.$get({ query: { ...filters, cursor: pageParam } })),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (last: KeysetPage<Vendor>) => last.nextCursor ?? undefined,
      staleTime: STALE.list,
    }),

  /** Keyset budget-lines list (encumbrance — remaining is derived server-side). */
  budgetLines: (filters: BudgetLineListQuery) =>
    queryOptions({
      queryKey: libraryKeys.acquisitions.budgetLines(filters),
      queryFn: () => unwrap<KeysetPage<BudgetLine>>(libApi.acquisitions["budget-lines"].$get({ query: filters })),
      staleTime: STALE.list,
      placeholderData: keepPreviousData,
    }),

  /** The budget register as an INFINITE stream (2026-09-22) — same endpoint, same keyset; BudgetLineListQuery is a bare cursor list, so the key is the limit alone. */
  budgetLinesInfinite: (filters: Omit<BudgetLineListQuery, "cursor">) =>
    infiniteQueryOptions({
      queryKey: [...libraryKeys.acquisitions.budgetLines(filters), "infinite"] as const,
      queryFn: ({ pageParam }) =>
        unwrap<KeysetPage<BudgetLine>>(
          libApi.acquisitions["budget-lines"].$get({ query: { ...filters, cursor: pageParam } }),
        ),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (last: KeysetPage<BudgetLine>) => last.nextCursor ?? undefined,
      staleTime: STALE.list,
    }),

  /** Keyset purchase-orders list (header projection — lines attach on the detail view). */
  purchaseOrders: (filters: POListQuery) =>
    queryOptions({
      queryKey: libraryKeys.acquisitions.purchaseOrders.list(filters),
      queryFn: () => unwrap<KeysetPage<PurchaseOrder>>(libApi.acquisitions["purchase-orders"].$get({ query: filters })),
      staleTime: STALE.list,
      placeholderData: keepPreviousData,
    }),

  /** The order register as an INFINITE stream (2026-09-22) — same endpoint, same keyset (created_at DESC, id); ?search/?status ⊂ the key, the cursor is not. */
  purchaseOrdersInfinite: (filters: Omit<POListQuery, "cursor">) =>
    infiniteQueryOptions({
      queryKey: [...libraryKeys.acquisitions.purchaseOrders.list(filters), "infinite"] as const,
      queryFn: ({ pageParam }) =>
        unwrap<KeysetPage<PurchaseOrder>>(
          libApi.acquisitions["purchase-orders"].$get({ query: { ...filters, cursor: pageParam } }),
        ),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (last: KeysetPage<PurchaseOrder>) => last.nextCursor ?? undefined,
      staleTime: STALE.list,
    }),

  /** A single PO with its lines attached (the receive/status detail). */
  purchaseOrder: (id: string) =>
    queryOptions({
      queryKey: libraryKeys.acquisitions.purchaseOrders.detail(id),
      queryFn: () => unwrap<PurchaseOrder>(libApi.acquisitions["purchase-orders"][":id"].$get({ param: { id } })),
      staleTime: STALE.detail,
    }),

  /** Receivable POs (Ordered/Partially Received with open lines) — the Receiving worklist, lines attached. */
  receivablePurchaseOrders: (filters: ReceivableListQuery) =>
    queryOptions({
      queryKey: libraryKeys.acquisitions.receivable(filters),
      queryFn: () => unwrap<KeysetPage<PurchaseOrder>>(libApi.acquisitions.receivable.$get({ query: filters })),
      staleTime: STALE.list,
      placeholderData: keepPreviousData,
    }),

  /** The receiving worklist as an INFINITE stream (2026-09-22) — same endpoint, same keyset, lines attached; a bare cursor list, so the key is the limit alone. */
  receivablePurchaseOrdersInfinite: (filters: Omit<ReceivableListQuery, "cursor">) =>
    infiniteQueryOptions({
      queryKey: [...libraryKeys.acquisitions.receivable(filters), "infinite"] as const,
      queryFn: ({ pageParam }) =>
        unwrap<KeysetPage<PurchaseOrder>>(
          libApi.acquisitions.receivable.$get({ query: { ...filters, cursor: pageParam } }),
        ),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (last: KeysetPage<PurchaseOrder>) => last.nextCursor ?? undefined,
      staleTime: STALE.list,
    }),

  /** One receipt document with its lines (the GRN detail modal). */
  goodsReceipt: (id: string) =>
    queryOptions({
      queryKey: libraryKeys.acquisitions.goodsReceipt(id),
      queryFn: () => {
        const args = { param: { id } };
        return unwrap<GoodsReceipt>(libApi.acquisitions["goods-receipts"][":id"].$get(args));
      },
      staleTime: STALE.detail,
    }),

  /** Keyset goods-receipts (GRN) log. */
  goodsReceipts: (filters: GRNListQuery) =>
    queryOptions({
      queryKey: libraryKeys.acquisitions.goodsReceipts(filters),
      queryFn: () => unwrap<KeysetPage<GoodsReceipt>>(libApi.acquisitions["goods-receipts"].$get({ query: filters })),
      staleTime: STALE.list,
      placeholderData: keepPreviousData,
    }),

  /** The GRN log as an INFINITE stream (2026-09-22) — same endpoint, same keyset (newest first); the key is the limit alone. */
  goodsReceiptsInfinite: (filters: Omit<GRNListQuery, "cursor">) =>
    infiniteQueryOptions({
      queryKey: [...libraryKeys.acquisitions.goodsReceipts(filters), "infinite"] as const,
      queryFn: ({ pageParam }) =>
        unwrap<KeysetPage<GoodsReceipt>>(
          libApi.acquisitions["goods-receipts"].$get({ query: { ...filters, cursor: pageParam } }),
        ),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (last: KeysetPage<GoodsReceipt>) => last.nextCursor ?? undefined,
      staleTime: STALE.list,
    }),

  // ══ Reports (membership revenue; catalogueStats + storageUsage live above) ══
  /** Membership revenue summary (KPIs + by-month/plan/method/audience; lib:reports). */
  /** Copies flagged Lost or Damaged, with the replacement fee (if any) that was raised. */
  flaggedCopies: () =>
    queryOptions({
      queryKey: libraryKeys.inventory.flagged(),
      queryFn: () => unwrap<FlaggedCopy[]>(libApi.circulation.inventory.flagged.$get()),
      staleTime: STALE.list,
    }),

  /**
   * The circulation rule matrix — one row per (patronRole, materialType) cell.
   * Whole-list, unpaged: the matrix is small by construction (PUT caps it at 100).
   */
  circulationRules: () =>
    queryOptions({
      queryKey: libraryKeys.policy.circulationRules(),
      queryFn: () => unwrap<CirculationRule[]>(libApi["circulation-rules"].$get()),
      staleTime: STALE.list,
    }),

  /** Closure/holiday calendar — dates that suspend circulation or fine accrual. */
  calendarExceptions: () =>
    queryOptions({
      queryKey: libraryKeys.policy.calendarExceptions(),
      queryFn: () => unwrap<CalendarException[]>(libApi["calendar-exceptions"].$get()),
      staleTime: STALE.list,
    }),

  /** Membership plan registry — the whole list; the API does not page it. */
  membershipPlans: () =>
    queryOptions({
      queryKey: libraryKeys.memberships.plans(),
      queryFn: () => unwrap<MembershipPlan[]>(libApi["membership-plans"].$get()),
      staleTime: STALE.list,
    }),

  /**
   * Derived member dues. NOT keyset — the service computes and sorts the whole
   * set (total-due DESC), so the FE filters/pages it in memory.
   */
  membershipDues: (filters: MemberBillingQuery) =>
    queryOptions({
      queryKey: libraryKeys.memberships.members(filters),
      queryFn: () => unwrap<BillingMember[]>(libApi.memberships.members.$get({ query: filters })),
      staleTime: STALE.list,
    }),

  /** The receipts ledger (lib:reports — financial reporting, not a desk read). */
  memberPayments: (filters: PaymentListQuery) =>
    queryOptions({
      queryKey: libraryKeys.memberships.payments(filters),
      queryFn: () => unwrap<MemberPayment[]>(libApi.memberships.payments.$get({ query: filters })),
      staleTime: STALE.list,
    }),

  revenueSummary: () =>
    queryOptions({
      queryKey: libraryKeys.memberships.revenue(),
      queryFn: () => unwrap<RevenueSummary>(libApi.memberships.revenue.$get()),
      staleTime: STALE.list,
    }),

  /** The audit-event log (keyset; lib:admin). */
  auditEvents: (filters: AuditListQuery) =>
    queryOptions({
      queryKey: libraryKeys.audit.list(filters),
      queryFn: () => unwrap<{ data: AuditEvent[]; nextCursor: string | null }>(libApi["audit-events"].$get({ query: filters })),
      staleTime: STALE.list,
      placeholderData: keepPreviousData,
    }),

  /**
   * The audit log as an INFINITE stream (2026-09-22). Same endpoint, same keyset
   * (newest first) — the natural shape for a log, which is read by scrolling
   * back through time. Module, severity and the search are the key; the cursor
   * is not.
   */
  auditEventsInfinite: (filters: Omit<AuditListQuery, "cursor">) =>
    infiniteQueryOptions({
      queryKey: [...libraryKeys.audit.list(filters), "infinite"] as const,
      queryFn: ({ pageParam }) =>
        unwrap<{ data: AuditEvent[]; nextCursor: string | null }>(
          libApi["audit-events"].$get({ query: { ...filters, cursor: pageParam } }),
        ),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (last: { nextCursor: string | null }) => last.nextCursor ?? undefined,
      staleTime: STALE.list,
    }),

  /** The log's figures — GET /audit-events/stats: every event ever recorded, by severity. */
  auditEventStats: () =>
    queryOptions({
      queryKey: libraryKeys.audit.stats(),
      queryFn: () => unwrap<AuditEventStats>(libApi["audit-events"].stats.$get()),
      staleTime: STALE.list,
    }),

  /** The MARC bulk-import history (keyset, newest first; lib:catalog.read). */
  importBatches: (filters: ImportBatchListQuery) =>
    queryOptions({
      queryKey: libraryKeys.catalogue.importBatches.list(filters),
      queryFn: () => unwrap<KeysetPage<ImportBatch>>(libApi.catalogue["import-batches"].$get({ query: filters })),
      staleTime: STALE.list,
      placeholderData: keepPreviousData,
    }),

  /** The MARC import history as an INFINITE stream (2026-09-22) — same endpoint, same keyset (newest first); the key is the limit alone. */
  importBatchesInfinite: (filters: Omit<ImportBatchListQuery, "cursor">) =>
    infiniteQueryOptions({
      queryKey: [...libraryKeys.catalogue.importBatches.list(filters), "infinite"] as const,
      queryFn: ({ pageParam }) =>
        unwrap<KeysetPage<ImportBatch>>(
          libApi.catalogue["import-batches"].$get({ query: { ...filters, cursor: pageParam } }),
        ),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (last: KeysetPage<ImportBatch>) => last.nextCursor ?? undefined,
      staleTime: STALE.list,
    }),

  /** The institution-settings singleton (lib:admin — the settings console). */
  libSettings: () =>
    queryOptions({
      queryKey: libraryKeys.settings.institution(),
      queryFn: () => unwrap<LibSettings>(libApi.settings.$get()),
      staleTime: STALE.detail,
    }),

  // ── Parental controls (bare-array registries; lib:patrons.read) ──
  parentalProfiles: () =>
    queryOptions({
      queryKey: libraryKeys.parental.profiles(),
      queryFn: () => unwrap<ParentalProfile[]>(libApi["parental-profiles"].$get()),
      staleTime: STALE.list,
    }),
  guardianLinks: () =>
    queryOptions({
      queryKey: libraryKeys.parental.links(),
      queryFn: () => unwrap<GuardianLink[]>(libApi["guardian-links"].$get()),
      staleTime: STALE.list,
    }),

  // ── /reports/* — the per-domain aggregate consoles (all lib:reports). ──
  reportOverview: () =>
    queryOptions({
      queryKey: libraryKeys.reports.overview(),
      queryFn: () => unwrap<ReportOverview>(libApi.reports.overview.$get()),
      staleTime: STALE.list,
    }),
  reportCirculation: () =>
    queryOptions({
      queryKey: libraryKeys.reports.circulation(),
      queryFn: () => unwrap<ReportCirculation>(libApi.reports.circulation.$get()),
      staleTime: STALE.list,
    }),
  reportCatalogue: () =>
    queryOptions({
      queryKey: libraryKeys.reports.catalogue(),
      queryFn: () => unwrap<ReportCatalogue>(libApi.reports.catalogue.$get()),
      staleTime: STALE.list,
    }),
  reportFines: () =>
    queryOptions({
      queryKey: libraryKeys.reports.fines(),
      queryFn: () => unwrap<ReportFines>(libApi.reports.fines.$get()),
      staleTime: STALE.list,
    }),
  reportMembers: () =>
    queryOptions({
      queryKey: libraryKeys.reports.members(),
      queryFn: () => unwrap<ReportMembers>(libApi.reports.members.$get()),
      staleTime: STALE.list,
    }),
  reportAcquisitions: () =>
    queryOptions({
      queryKey: libraryKeys.reports.acquisitions(),
      queryFn: () => unwrap<ReportAcquisitions>(libApi.reports.acquisitions.$get()),
      staleTime: STALE.list,
    }),
  reportDigital: () =>
    queryOptions({
      queryKey: libraryKeys.reports.digital(),
      queryFn: () => unwrap<ReportDigital>(libApi.reports.digital.$get()),
      staleTime: STALE.list,
    }),
} as const;

export type LibraryQueries = typeof libraryQueries;
